(() => {
  'use strict';

  const arrowIcon = (direction) => {
    const points = direction === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    return `<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="${points}"></polyline></svg>`;
  };

  const nearestIndex = (scroller, items) => {
    const left = scroller.scrollLeft;
    let selected = 0;
    let distance = Infinity;
    items.forEach((item, index) => {
      const delta = Math.abs(item.offsetLeft - left);
      if (delta < distance) {
        distance = delta;
        selected = index;
      }
    });
    return selected;
  };

  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-btb-gallery-track]');
      this.slides = Array.from(root.querySelectorAll('[data-btb-gallery-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-btb-thumb]'));
      this.dots = Array.from(root.querySelectorAll('[data-btb-gallery-dot]'));
      this.counter = root.querySelector('[data-btb-gallery-counter]');
      this.prev = root.querySelector('[data-btb-gallery-prev]');
      this.next = root.querySelector('[data-btb-gallery-next]');
      this.index = 0;
      this.frame = null;
      this.touchStartX = null;
      this.touchStartY = null;
      this.touchStartIndex = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.track || this.slides.length < 1) return;
      this.bind();
      this.update(0, false);
      this.preload(1);
    }

    bind() {
      this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.root.addEventListener('basictex:gallery-go-to', (event) => {
        const requested = Number(event.detail?.index);
        if (Number.isFinite(requested)) this.goTo(requested);
      });
      this.track.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        if (!touch) return;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchStartIndex = this.index;
      }, { passive: true });
      this.track.addEventListener('touchend', (event) => {
        if (this.touchStartX === null || this.touchStartY === null) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const distanceX = touch.clientX - this.touchStartX;
        const distanceY = touch.clientY - this.touchStartY;
        this.touchStartX = null;
        this.touchStartY = null;
        if (Math.abs(distanceX) < 38 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
        this.goTo(this.touchStartIndex + (distanceX < 0 ? 1 : -1));
      }, { passive: true });
      this.track.addEventListener('touchcancel', () => {
        this.touchStartX = null;
        this.touchStartY = null;
      }, { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1);
        }
      });
      this.track.addEventListener('scroll', () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update(nearestIndex(this.track, this.slides)));
      }, { passive: true });
      window.addEventListener('resize', () => this.goTo(this.index, false), { passive: true });
    }

    preload(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      if (typeof image.decode === 'function') image.decode().catch(() => {});
    }

    goTo(index, smooth = true) {
      const bounded = Math.max(0, Math.min(index, this.slides.length - 1));
      this.preload(bounded);
      this.preload(bounded + 1);
      const behavior = smooth && !this.reducedMotion.matches ? 'smooth' : 'auto';
      this.track.scrollTo({ left: this.slides[bounded].offsetLeft, behavior });
      this.update(bounded);
    }

    update(index) {
      this.index = index;
      this.thumbs.forEach((thumb, itemIndex) => {
        const active = itemIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.dots.forEach((dot, itemIndex) => {
        const active = itemIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;
      this.preload(index + 1);
    }
  }

  class NativeCarousel {
    constructor(rail) {
      this.rail = rail;
      this.items = Array.from(rail.children);
      if (this.items.length < 2 || rail.closest('.bt-carousel-shell')) return;
      this.shell = document.createElement('div');
      this.shell.className = 'bt-carousel-shell';
      rail.parentNode.insertBefore(this.shell, rail);
      this.shell.appendChild(rail);
      this.prev = this.button('prev', 'Anterior');
      this.next = this.button('next', 'Siguiente');
      this.shell.append(this.prev, this.next);
      this.prev.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      rail.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.update();
    }

    button(direction, label) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `bt-carousel-arrow bt-carousel-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = arrowIcon(direction);
      return button;
    }

    move(direction) {
      const current = nearestIndex(this.rail, this.items);
      const target = Math.max(0, Math.min(current + direction, this.items.length - 1));
      this.rail.scrollTo({ left: this.items[target].offsetLeft, behavior: 'smooth' });
    }

    update() {
      const max = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth - 2);
      this.prev.disabled = this.rail.scrollLeft <= 2;
      this.next.disabled = this.rail.scrollLeft >= max;
      const overflow = max > 2;
      this.prev.hidden = !overflow;
      this.next.hidden = !overflow;
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-btb-gallery]').forEach((gallery) => {
      if (!gallery.dataset.btbReady) {
        gallery.dataset.btbReady = 'true';
        new ProductGallery(gallery);
      }
    });
    scope.querySelectorAll?.('[data-bt-native-carousel]').forEach((rail) => new NativeCarousel(rail));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
